import { useEffect, useMemo, useRef, useState } from "react";
import {
  LayoutDashboard,
  Coins,
  History,
  Settings,
  Menu,
  X,
  LogOut,
  Bell,
  Wallet,
  User,
  RefreshCwOff,
  Diamond,
  CheckCheck,
  Copy,
  List,
} from "lucide-react";
import BingoldLogo from "@/components/BingoldLogo";
import { Button } from "@/components/ui/button";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { DASHBOARD_PATH, LOCALSTORAGES } from "@/constants/constant";
import { ConnectButton } from "@rainbow-me/rainbowkit";
import { useAccount, useDisconnect } from "wagmi";
import {
  useClearNotificationApiCallMutation,
  useDeleteNotificationApiCallMutation,
  useGetListNotificationApiCallQuery,
  useGetProfileApiCallQuery,
  useGetReadNotificationApiCallMutation,
  useGetReadStatusNotificationApiCallQuery,
} from "@/services/api/auth/auth.api";
import { toast } from "sonner";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { ApiError, Notification } from "@/services/api/auth/auth.type";

interface DashboardLayoutProps {
  children?: React.ReactNode;
  activeScreen?: string;
  onScreenChange?: (screen: string) => void;
  walletAddress?: string;
}

export default function DashboardLayout({
  children,
  activeScreen,
  onScreenChange,
  walletAddress = "0x1234...5678",
}: DashboardLayoutProps) {
  const { isConnected, isDisconnected, isConnecting, isReconnecting, address } =
    useAccount();
  const { disconnect } = useDisconnect();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const loaction = useLocation();
  const navigate = useNavigate();
  const [referralLink, setRerrakLink] = useState("");
  const [copiedReferral, setCopiedReferral] = useState(false);

  const [openNotificationPopup, setOpenNotificationPopup] = useState(false);
  const [notification, setNotification] = useState<Notification[]>([]);
  const popupRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const wasConnectedRef = useRef(false); // tracks if user ever connected in this session
  const hydratedRef = useRef(false);
  const [page, setPage] = useState(1);

  const location = useLocation();

  const { data: userProfile } = useGetProfileApiCallQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });
  const { data: readStatus } = useGetReadStatusNotificationApiCallQuery();
  const { data: listNotification, isFetching } =
    useGetListNotificationApiCallQuery({
      page: page,
      limit: 10,
    });

  const [readNotification] = useGetReadNotificationApiCallMutation();
  const [deleteNotification] = useDeleteNotificationApiCallMutation();
  const [clearAllNotification] = useClearNotificationApiCallMutation();
  const isKOL = userProfile?.result?.isKol === true;

  const navigation = useMemo(() => {
    return [
      {
        id: "dashboard",
        name: "Dashboard",
        icon: LayoutDashboard,
        badge: null,
        href: DASHBOARD_PATH.ROOT,
      },
      {
        id: "campaigns",
        name: "All Campaigns",
        icon: User,
        badge: "",
        href: DASHBOARD_PATH.ALL_CAMPAIGNS,
      },

      ...(!isKOL
        ? [
            {
              id: "private_campaigns",
              name: "Private Campaigns",
              icon: User,
              badge: "",
              href: DASHBOARD_PATH.ALL_Private_CAMPAIGNS,
            },
          ]
        : [
            {
              id: "list_campaigns",
              name: "List Campaigns",
              icon: List,
              badge: "",
              href: DASHBOARD_PATH.ALL_List_CAMPAIGNS,
            },
          ]),

      {
        id: "history",
        name: "Claim History",
        icon: History,
        badge: null,
        href: DASHBOARD_PATH.CLAIM_HISTORY,
      },
      {
        id: "settings",
        name: "Profile & Settings",
        icon: Settings,
        badge: null,
        href: DASHBOARD_PATH.PORFILE_SETTINGS,
      },
      {
        id: "refferal",
        name: "Referral Details",
        icon: Diamond,
        badge: null,
        href: DASHBOARD_PATH.REFFERAL,
      },
    ];
  }, [isKOL, userProfile]);

  const handleDisconnect = () => {
    disconnect();
    localStorage.removeItem("shouldConnectSocket");
    localStorage.removeItem("redeemCode");

    localStorage.removeItem(LOCALSTORAGES.TOKEN);
    navigate(DASHBOARD_PATH.ROOT);
  };

  const handleOpenNotificationPopup = async () => {
    setOpenNotificationPopup((prev) => !prev);
    try {
      const response = await readNotification().unwrap();
      if (response.responseCode === 200) {
        // toast.success(response.responseMessage);
      }
    } catch (error) {
      const fetchError = error as FetchBaseQueryError;
      const errorData = fetchError.data as ApiError;
      toast.error(errorData?.responseMessage || "Error reading notification");
    }
  };

  const handleDeleteNotification = async (id: string) => {
    try {
      const respone = await deleteNotification({
        id,
      }).unwrap();
      console.log(respone);
      if (respone.responseCode === 200) {
        toast.success(respone.responseMessage);
        setNotification((prev) => prev.filter((noti) => noti._id !== id));
      } else {
        toast.error(respone.responseMessage);
      }
    } catch (error) {
      const fetchError = error as FetchBaseQueryError;
      const errorData = fetchError.data as ApiError;
      toast.error(errorData?.responseMessage || "Error deleting notification");
    }
  };

  const handleClearAllNotification = async () => {
    try {
      const respone = await clearAllNotification().unwrap();
      console.log(respone);
      if (respone.responseCode === 200) {
        toast.success(respone.responseMessage);
        setNotification([]);
      } else {
        toast.error(respone.responseMessage);
      }
    } catch (error) {
      const fetchError = error as FetchBaseQueryError;
      const errorData = fetchError.data as ApiError;
      toast.error(errorData?.responseMessage || "Error deleting notification");
    }
  };

  const handleCopyReferral = () => {
    navigator.clipboard.writeText(referralLink);
    setCopiedReferral(true);
    setTimeout(() => setCopiedReferral(false), 2000);
  };

  useEffect(() => {
    if (listNotification?.result?.docs) {
      if (page === 1) {
        setNotification(listNotification.result.docs);
      } else {
        setNotification((prev) => [
          ...prev,
          ...(listNotification?.result?.docs || []),
        ]);
      }
    }
    // if (
    //   listNotification &&
    //   listNotification?.result?.docs &&
    //   listNotification?.result?.docs.length > 0
    // ) {
    //   setNotification((prev) => {
    //     return [...prev, ...listNotification?.result?.docs];
    //   });
    // }
  }, [listNotification, page]);

  useEffect(() => {
    if (!listRef.current) return; // ✅ ensure ref exists

    const ul = listRef.current;

    const handleScroll = () => {
      if (!ul || isFetching) return;

      const { scrollTop, scrollHeight, clientHeight } = ul;
      console.log(scrollHeight, scrollTop, clientHeight);

      if (scrollTop + clientHeight >= scrollHeight - 5) {
        console.log("aaya");
        if (listNotification?.result?.page) {
          setPage((prev) => prev + 1);
        }
      }
    };

    ul.addEventListener("scroll", handleScroll);
    console.log("Scroll listener added ✅");

    return () => {
      ul.removeEventListener("scroll", handleScroll);
      console.log("Scroll listener removed ❌");
    };
  }, [listNotification, isFetching, openNotificationPopup]);

  useEffect(() => {
    if (!isConnecting && !isReconnecting) hydratedRef.current = true;
  }, [isConnecting, isReconnecting]);

  // run only on actual transition connected -> disconnected
  useEffect(() => {
    if (!hydratedRef.current) return;

    if (isConnected) {
      wasConnectedRef.current = true;
      return;
    }

    if (wasConnectedRef.current && isDisconnected) {
      // ✅ true disconnect event (not initial state)
      localStorage.removeItem("shouldConnectSocket");
      localStorage.removeItem("redeemCode");
      localStorage.removeItem(LOCALSTORAGES.TOKEN);
      navigate("/");
    }
  }, [isConnected, isDisconnected, navigate]);

  // useEffect(() => {
  //   console.log(status);
  //   const token = localStorage.getItem(LOCALSTORAGES.TOKEN);
  //   if (!token) {
  //     localStorage.removeItem(LOCALSTORAGES.TOKEN);
  //     navigate("/");
  //   }
  //   if (isDisconnected) {
  //     localStorage.removeItem(LOCALSTORAGES.TOKEN);
  //     navigate("/"); // Redirect after clearing
  //   }
  // }, [status]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        popupRef.current &&
        !popupRef.current.contains(event.target as Node)
      ) {
        setOpenNotificationPopup(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (typeof window !== "undefined") {
      console.log(userProfile);
      const domain = window.location.origin; // e.g. https://example.com
      setRerrakLink(`${domain}/ref/${userProfile?.result?.referralCode}`);
    }
  }, [userProfile]);

  const currentPageName =
    navigation.find((item) => item.href === location.pathname)?.name ||
    "Dashboard";

  return (
    <div className="h-screen bg-black text-white flex overflow-hidden">
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      {/* Sidebar */}
      <div
        className={`
        fixed inset-y-0 left-0 z-50 w-64 flex flex-col bg-gray-900 border-r border-yellow-500/20 
        transform transition-transform duration-300 ease-in-out 
        lg:relative lg:translate-x-0 lg:transition-none lg:transform-none
        ${
          sidebarOpen
            ? "translate-x-0  max-w-96 w-full"
            : "-translate-x-full lg:translate-x-0 "
        }
      `}
      >
        {/* Sidebar Header */}
        <div className="flex items-center justify-between p-5 border-b border-yellow-500/20">
          <Link to={"/"}>
            <BingoldLogo size="medium" />
          </Link>
          <Button
            variant="ghost"
            size="sm"
            className="lg:hidden text-white hover:bg-gray-800"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="w-4 h-4" />
          </Button>
        </div>

        {/* Wallet Info */}
        {/* <div className="p-5 border-b border-yellow-500/20">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-r from-yellow-400 to-yellow-600 rounded-full flex items-center justify-center flex-shrink-0">
              <Wallet className="w-5 h-5 text-black" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm text-white truncate">{address}</div>
              <div className="text-xs text-gray-400">Connected</div>
            </div>
          </div>
        </div> */}

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-5 overflow-y-auto">
          {navigation.map((item) => {
            const Icon = item.icon;
            const isActive = item.href === location.pathname;

            return (
              <Link
                to={item.href}
                key={item.id}
                onClick={() => {
                  // onScreenChange(item.id);
                  setSidebarOpen(false);
                }}
                className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors ${
                  isActive
                    ? "bg-yellow-500/20 text-yellow-400 border border-yellow-500/30"
                    : "text-gray-300 hover:bg-gray-800 hover:text-white"
                }`}
              >
                <Icon className="w-5 h-5 flex-shrink-0" />
                <span className="flex-1 text-left truncate">{item.name}</span>
                {item.badge && (
                  <Badge className="bg-yellow-500 text-black text-xs flex-shrink-0 rounded-full">
                    {item.badge}
                  </Badge>
                )}
              </Link>
            );
          })}

          {referralLink && (
            <div className="md:hidden flex space-x-2 items-center">
              <div className="flex-1 bg-secondary/50 rounded-lg p-3">
                <p className="text-xs text-white truncate">{referralLink}</p>
              </div>
              <Button
                size="sm"
                onClick={handleCopyReferral}
                className={`cursor-pointer ${
                  copiedReferral
                    ? "bg-green-500/20 text-green-400 border border-green-500/30"
                    : "bg-primary hover:bg-primary/90 text-primary-foreground"
                }`}
              >
                {copiedReferral ? (
                  <CheckCheck className="h-4 w-4" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </Button>
            </div>
          )}
        </nav>

        {/* Sidebar Footer */}
        <div className="p-4 border-t border-yellow-500/20">
          <Button
            variant="outline"
            className="w-full border-red-500/30 text-red-400 hover:bg-red-500 hover:text-white cursor-pointer"
            onClick={handleDisconnect}
          >
            <LogOut className="w-4 h-4 mr-2" />
            Disconnect
          </Button>
        </div>
      </div>
      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-w-0 lg:ml-0 hide-scrollbar">
        {/* Top Header */}
        <header className="bg-gray-900/50 border-b border-yellow-500/20 p-5.5 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                size="sm"
                className="lg:hidden text-white hover:bg-gray-800"
                onClick={() => setSidebarOpen(true)}
              >
                <Menu className="w-5 h-5" />
              </Button>
              <h1 className="text-xl text-white font-medium">
                {currentPageName}
              </h1>
            </div>

            <div className="flex items-center space-x-4">
              <div ref={popupRef} className="relative">
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-gray-300 hover:text-white relative cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenNotificationPopup();
                  }}
                >
                  <Bell className="w-5 h-5" />
                  {readStatus && !readStatus?.result && (
                    <div className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-500 rounded-full"></div>
                  )}
                </Button>

                {/* Popup */}
                {openNotificationPopup && (
                  <div className="fixed flex flex-col right-0 top-0 h-screen max-w-96 w-full bg-white shadow-lg overflow-hidden z-50 pb-2">
                    <div className="flex justify-end text-black hover:text-gray-500 cursor-pointer px-4 py-2 text-lg font-bold">
                      <span onClick={() => setOpenNotificationPopup(false)}>
                        X
                      </span>
                    </div>

                    <div className="p-4 border-b border-gray-200 font-semibold text-gray-800 flex justify-between items-center">
                      <p>Notifications</p>

                      <Button
                        onClick={handleClearAllNotification}
                        className="cursor-pointer"
                        disabled={notification?.length === 0}
                      >
                        Clear All
                      </Button>
                    </div>

                    <ul
                      ref={listRef}
                      className="flex-1 overflow-y-auto space-y-4 py-1.5 pb-4"
                    >
                      {notification?.length > 0 &&
                        notification.map((noti) => (
                          <li
                            className={`text-gray-800 space-y-1 px-4 py-1 flex justify-between items-center gap-3 ${
                              noti.isRead ? "bg-blue-50" : "bg-gray-100"
                            }`}
                          >
                            <div>
                              <h3 className="text-sm font-bold">
                                {noti.title}
                              </h3>
                              <p className="text-xs font-semibold">
                                {noti.description}
                              </p>
                            </div>

                            <span
                              onClick={() => handleDeleteNotification(noti._id)}
                              className="cursor-pointer font-medium tex-xs inline-block"
                            >
                              X
                            </span>
                          </li>
                        ))}
                      {notification?.length === 0 && (
                        <li className="text-gray-800 space-y-1 px-4 py-1 flex justify-between items-center gap-3">
                          <div>
                            <h3 className="text-sm font-bold text-center">
                              No Notification
                            </h3>
                          </div>
                        </li>
                      )}

                      {isFetching && (
                        <li className="text-center text-gray-500 py-2">
                          Loading...
                        </li>
                      )}
                    </ul>

                    {/* <div className="p-2 text-center text-blue-500 text-sm hover:underline cursor-pointer">
                      View all
                    </div> */}
                  </div>
                )}
              </div>

              {referralLink && (
                <div className="md:flex space-x-2 items-center hidden">
                  <div className="flex-1 bg-secondary/50 rounded-lg p-3">
                    <p className="text-sm text-white truncate">
                      {referralLink}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    onClick={handleCopyReferral}
                    className={`cursor-pointer ${
                      copiedReferral
                        ? "bg-green-500/20 text-green-400 border border-green-500/30"
                        : "bg-primary hover:bg-primary/90 text-primary-foreground"
                    }`}
                  >
                    {copiedReferral ? (
                      <CheckCheck className="h-4 w-4" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              )}

              <ConnectButton showBalance={false} chainStatus={"none"} />
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-4 md:p-6 overflow-y-auto bg-[linear-gradient(-245deg,_#0d1523,_#050914,_#000000)]">
          <div className="max-w-7xl mx-auto">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
